/**
 * Role-based access control.
 *
 * A user holds one role per property (see the Membership model). Routes declare
 * the permission they need; the role matrix below is the only place that decides
 * who holds it. OWNER is the only role that can manage users and properties.
 */
export const ROLES = [
    'OWNER',
    'MANAGER',
    'ACCOUNTANT',
    'FRONT_DESK',
    'CASHIER',
    'STOREKEEPER',
    'KITCHEN',
    'HOUSEKEEPING',
];
export const PERMISSIONS = [
    'dashboard:view',
    'pos:operate',
    'pos:bill',
    'pos:discount',
    'pos:void',
    'kds:view',
    'kds:update',
    'menu:view',
    'menu:manage',
    'menu:price',
    'inventory:view',
    'inventory:adjust',
    'inventory:manage',
    'procurement:view',
    'procurement:create',
    'procurement:approve',
    'procurement:receive',
    'rooms:view',
    'rooms:checkin',
    'rooms:checkout',
    'rooms:manage',
    'housekeeping:view',
    'housekeeping:update',
    'reservation:view',
    'reservation:manage',
    'invoice:view',
    'invoice:void',
    'hr:view',
    'hr:manage',
    'payroll:view',
    'payroll:run',
    'reports:view',
    'gst:export',
    'settings:view',
    'settings:manage',
    'users:view',
    'users:manage',
    'audit:view',
    'property:manage',
];
const FRONT_OF_HOUSE = [
    'dashboard:view',
    'pos:operate',
    'pos:bill',
    'kds:view',
    'menu:view',
    'invoice:view',
    'reservation:view',
    'reservation:manage',
];
export const ROLE_PERMISSIONS = {
    OWNER: [...PERMISSIONS],
    /*
     * Everything on the floor, and nothing under Administration.
     *
     * Payments, users, the property profile and the audit trail belong to the
     * owner. `settings:view` stays, because the menu screen reads the target
     * food cost through it and the QR sheet reads the menu link - taking it
     * away would break two screens a manager runs every day.
     */
    MANAGER: PERMISSIONS.filter((p) => ![
        'users:view',
        'users:manage',
        'settings:manage',
        'audit:view',
        'property:manage',
    ].includes(p)),
    ACCOUNTANT: [
        'dashboard:view',
        'invoice:view',
        'reports:view',
        'gst:export',
        'payroll:view',
        'payroll:run',
        'hr:view',
        'procurement:view',
        'inventory:view',
        'menu:view',
        'settings:view',
    ],
    FRONT_DESK: [
        ...FRONT_OF_HOUSE,
        'rooms:view',
        'rooms:checkin',
        'rooms:checkout',
        'housekeeping:view',
    ],
    CASHIER: [...FRONT_OF_HOUSE, 'pos:discount', 'rooms:view'],
    STOREKEEPER: [
        'dashboard:view',
        'inventory:view',
        'inventory:adjust',
        'inventory:manage',
        'procurement:view',
        'procurement:create',
        'procurement:receive',
        'menu:view',
        'reports:view',
    ],
    KITCHEN: ['kds:view', 'kds:update', 'menu:view', 'inventory:view'],
    HOUSEKEEPING: ['housekeeping:view', 'housekeeping:update', 'rooms:view'],
};
export const roleHas = (role, permission) => ROLE_PERMISSIONS[role].includes(permission);
export const permissionsFor = (role) => [...ROLE_PERMISSIONS[role]];
export const ROLE_LABELS = {
    OWNER: 'Owner',
    MANAGER: 'General manager',
    ACCOUNTANT: 'Accountant',
    FRONT_DESK: 'Front desk',
    CASHIER: 'Cashier',
    STOREKEEPER: 'Storekeeper',
    KITCHEN: 'Kitchen',
    HOUSEKEEPING: 'Housekeeping',
};
export const ROLE_DESCRIPTIONS = {
    OWNER: 'Full access, including users, properties and tenant settings.',
    MANAGER: 'Runs the property day to day; everything except user and property administration.',
    ACCOUNTANT: 'Read-only on operations, with payroll runs and GST filing exports.',
    FRONT_DESK: 'Room rack, check-in and check-out, reservations, restaurant tickets.',
    CASHIER: 'Opens and settles restaurant tickets, applies discounts.',
    STOREKEEPER: 'Stock counts, wastage, purchase orders and goods receipts.',
    KITCHEN: 'Kitchen display: sees and advances tickets for its stations.',
    HOUSEKEEPING: 'Room cleaning tasks and housekeeping status only.',
};
//# sourceMappingURL=permissions.js.map